import {
  addDoc,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db, hasFirebaseConfig } from "../firebaseClient";

const LOCAL_STORAGE_KEY = "grocery_items_v1";
const LOCAL_USAGE_KEY = "grocery_usage_hourly_v1";

const USAGE_METRIC_KEYS = new Set(["reads", "writes", "deletes"]);

const toHourIso = (date = new Date()) => {
  const next = new Date(date);
  next.setMinutes(0, 0, 0);
  return next.toISOString();
};

const toHourLabel = (isoTime) =>
  new Intl.DateTimeFormat("en-PH", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(isoTime));

const readLocalUsage = () => {
  try {
    const raw = localStorage.getItem(LOCAL_USAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.error("Failed to read local grocery usage", error);
    return {};
  }
};

const writeLocalUsage = (usage) => {
  localStorage.setItem(LOCAL_USAGE_KEY, JSON.stringify(usage));
};

const incrementLocalUsage = (metric, amount = 1) => {
  if (!USAGE_METRIC_KEYS.has(metric)) {
    return;
  }

  const hourKey = toHourIso();
  const usage = readLocalUsage();
  const bucket = usage[hourKey] || { reads: 0, writes: 0, deletes: 0 };
  bucket[metric] = Number(bucket[metric] || 0) + amount;
  usage[hourKey] = bucket;
  writeLocalUsage(usage);
};

const trackUsageMetric = async (metric, amount = 1) => {
  if (!USAGE_METRIC_KEYS.has(metric)) {
    return;
  }

  if (!hasFirebaseConfig || !db) {
    incrementLocalUsage(metric, amount);
    return;
  }

  const hourKey = toHourIso();

  try {
    const ref = doc(db, "groceryUsageHourly", hourKey);
    await setDoc(
      ref,
      {
        hourKey,
        [metric]: increment(amount),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (error) {
    // Fall back locally when metrics collection is blocked by rules.
    console.error(
      "Failed to write cloud usage metric, using local fallback",
      error,
    );
    incrementLocalUsage(metric, amount);
  }
};

const buildLast24HourPoints = (sourceMap) => {
  const now = new Date();
  now.setMinutes(0, 0, 0);

  const points = [];

  for (let i = 23; i >= 0; i -= 1) {
    const date = new Date(now);
    date.setHours(now.getHours() - i);
    const key = date.toISOString();
    const bucket = sourceMap[key] || {};

    points.push({
      ts: key,
      label: toHourLabel(key),
      reads: Number(bucket.reads || 0),
      writes: Number(bucket.writes || 0),
      deletes: Number(bucket.deletes || 0),
    });
  }

  return points;
};

const toUsagePayload = (sourceMap, source) => {
  const points = buildLast24HourPoints(sourceMap);
  const totals = points.reduce(
    (acc, point) => ({
      reads: acc.reads + point.reads,
      writes: acc.writes + point.writes,
      deletes: acc.deletes + point.deletes,
    }),
    { reads: 0, writes: 0, deletes: 0 },
  );

  return {
    points,
    totals,
    source,
    updatedAt: new Date().toISOString(),
  };
};

const normalizeItem = (item) => ({
  id: item.id,
  name: item.name,
  quantity: Number(item.quantity) || 0,
  price: Number(item.price) || 0,
  image: item.image || "",
  nameLower: String(item.name || "").toLowerCase(),
  createdAt: item.createdAt || Date.now(),
});

const readLocalItems = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map(normalizeItem);
  } catch (error) {
    console.error("Failed to read local grocery items", error);
    return [];
  }
};

const writeLocalItems = (items) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
};

const parseFirestoreTimestamp = (value) => {
  if (!value) {
    return Date.now();
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  return Number(value) || Date.now();
};

export const addGroceryItem = async ({ name, quantity, price, image }) => {
  const safeName = String(name || "").trim();
  const parsedQuantity = Number(quantity);
  const safeQuantity =
    Number.isFinite(parsedQuantity) && parsedQuantity >= 1 ? parsedQuantity : 1;
  const safePrice = Math.round(Number(price) * 100) / 100;
  const safeImage = String(image || "").trim();

  const item = normalizeItem({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: safeName,
    quantity: safeQuantity,
    price: safePrice,
    image: safeImage,
    createdAt: Date.now(),
  });

  if (hasFirebaseConfig && db) {
    await addDoc(collection(db, "groceryItems"), {
      name: item.name,
      nameLower: item.nameLower,
      quantity: item.quantity,
      price: item.price,
      image: item.image,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await trackUsageMetric("writes");

    return item;
  }

  const localItems = readLocalItems();
  localItems.unshift(item);
  writeLocalItems(localItems);
  await trackUsageMetric("writes");

  return item;
};

export const updateGroceryItem = async (id, { name, price, image }) => {
  const safeName = String(name || "").trim();
  const safePrice = Math.round(Number(price) * 100) / 100;
  const safeImage = String(image || "").trim();

  if (hasFirebaseConfig && db) {
    const ref = doc(db, "groceryItems", id);
    await updateDoc(ref, {
      name: safeName,
      nameLower: safeName.toLowerCase(),
      price: safePrice,
      image: safeImage,
      updatedAt: serverTimestamp(),
    });

    await trackUsageMetric("writes");
    return;
  }

  const localItems = readLocalItems();
  const idx = localItems.findIndex((item) => item.id === id);
  if (idx !== -1) {
    localItems[idx] = normalizeItem({
      ...localItems[idx],
      name: safeName,
      price: safePrice,
      image: safeImage,
    });
    writeLocalItems(localItems);
    await trackUsageMetric("writes");
  }
};

export const getGroceryItems = async () => {
  if (hasFirebaseConfig && db) {
    const q = query(
      collection(db, "groceryItems"),
      orderBy("updatedAt", "desc"),
      limit(300),
    );

    const snapshot = await getDocs(q);
    const items = snapshot.docs.map((doc) => {
      const data = doc.data();
      return normalizeItem({
        id: doc.id,
        ...data,
        createdAt: parseFirestoreTimestamp(data.updatedAt || data.createdAt),
      });
    });

    await trackUsageMetric("reads");
    return items;
  }

  const items = readLocalItems();
  await trackUsageMetric("reads");
  return items;
};

export const getGroceryItemsCount = async () => {
  if (hasFirebaseConfig && db) {
    try {
      const q = query(collection(db, "groceryItems"));
      const snapshot = await getCountFromServer(q);
      return Number(snapshot.data().count || 0);
    } catch (error) {
      console.error("Failed to read cloud grocery item count", error);
    }
  }

  return readLocalItems().length;
};

export const getGroceryUsageStatsLast24Hours = async () => {
  if (hasFirebaseConfig && db) {
    try {
      const q = query(
        collection(db, "groceryUsageHourly"),
        orderBy("hourKey", "desc"),
        limit(72),
      );

      const snapshot = await getDocs(q);
      const map = {};

      snapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const hourKey = String(data.hourKey || docSnapshot.id || "");
        if (!hourKey) {
          return;
        }

        map[hourKey] = {
          reads: Number(data.reads || 0),
          writes: Number(data.writes || 0),
          deletes: Number(data.deletes || 0),
        };
      });

      return toUsagePayload(map, "app-tracked (firebase)");
    } catch (error) {
      console.error(
        "Failed to read cloud usage stats, using local fallback",
        error,
      );
    }
  }

  return toUsagePayload(readLocalUsage(), "app-tracked (local)");
};

export const isCloudBacked = () => hasFirebaseConfig && Boolean(db);

// ── Saved cart (per-user) ────────────────────────────────────────────────────

const LOCAL_CART_KEY = "grocery_cart_v1";

const readLocalCart = () => {
  try {
    const raw = localStorage.getItem(LOCAL_CART_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

const writeLocalCart = (cart) => {
  localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(cart));
};

export const saveCart = async (userId, cart) => {
  // Always persist locally for instant restore
  writeLocalCart(cart);

  if (hasFirebaseConfig && db && userId) {
    try {
      console.log(`[saveCart] Writing to Firestore: userCarts/${userId}`, {
        cart,
        userId,
      });
      const ref = doc(db, "userCarts", userId);
      await setDoc(ref, { cart, updatedAt: serverTimestamp() });
      console.log(`[saveCart] Successfully saved to Firestore`);
    } catch (error) {
      console.error(
        "Failed to save cart to Firestore, kept in localStorage",
        error,
      );
    }
  } else {
    console.log(
      `[saveCart] Firestore write skipped: hasFirebaseConfig=${hasFirebaseConfig}, db=${Boolean(db)}, userId=${userId}`,
    );
  }
};

export const loadCart = async (userId) => {
  if (hasFirebaseConfig && db && userId) {
    try {
      const ref = doc(db, "userCarts", userId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        if (data.cart && typeof data.cart === "object") {
          writeLocalCart(data.cart); // sync to local
          return data.cart;
        }
      }
    } catch (error) {
      console.error(
        "Failed to load cart from Firestore, using localStorage",
        error,
      );
    }
  }

  return readLocalCart();
};

export const clearSavedCart = async (userId) => {
  writeLocalCart({});

  if (hasFirebaseConfig && db && userId) {
    try {
      const ref = doc(db, "userCarts", userId);
      await setDoc(ref, { cart: {}, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error("Failed to clear cart in Firestore", error);
    }
  }
};
